import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Bot, Search, UtensilsCrossed, Swords } from 'lucide-react';
import { useT } from '../i18n';
import HeroShowcase from './HeroShowcase';
import PhoneSimulation from './PhoneSimulation';
import { trackEvent } from '../lib/analytics';

// Flagship products surfaced as hero pills — these are the strategic platform products,
// not generic trust signals. Catering AI ships next quarter.
const PRODUCTS = {
  en: [
    { icon: Search, label: 'Index AI™', to: '/index-ai', soon: false },
    { icon: Bot, label: 'AI Workforce™', to: '/ai-agents-for-restaurants', soon: false },
    { icon: UtensilsCrossed, label: 'Catering AI™', to: '/ai/catering-ai', soon: true, soonLabel: 'Being finalized' },
  ],
  es: [
    { icon: Search, label: 'Index AI™', to: '/index-ai', soon: false },
    { icon: Bot, label: 'AI Workforce™', to: '/ai-agents-for-restaurants', soon: false },
    { icon: UtensilsCrossed, label: 'Catering AI™', to: '/ai/catering-ai', soon: true, soonLabel: 'Fase final' },
  ],
};

// Hero conversion module — teases the upcoming AI report platform while driving
// today's lead capture into the #audit form (prefilled with the restaurant name).
const HOOK = {
  en: {
    hook: "Do you know why you're not selling?",
    hookB: 'Think like your guest.',
    sub: 'The AI platform restaurants use to grow online discovery.',
    placeholder: 'yourrestaurant.com',
    cta: 'Scan my website free',
    note: 'Free · Real live scan · Kitxens Index AI Score™ across Google, ChatGPT & Gemini',
    vsQ: 'Is your competitor selling more than you?',
    vsB: "Discover what they're doing to sell more — and you're not.",
    vsCta: 'Compare me against them, free →',
  },
  es: {
    hook: '¿Sabes por qué no vendes?',
    hookB: 'Piensa como tu cliente.',
    sub: 'La plataforma de IA que los restaurantes usan para crecer en visibilidad online.',
    placeholder: 'turestaurante.com',
    cta: 'Escanear mi web gratis',
    note: 'Gratis · Escaneo real en vivo · Kitxens Index AI Score™ en Google, ChatGPT y Gemini',
    vsQ: '¿Tu competencia vende más que tú?',
    vsB: 'Descubre qué está haciendo para vender más — y tú no.',
    vsCta: 'Compárame con ellos gratis →',
  },
};

// Live "learning" ticker — reinforces the intelligence-that-grows story.
function LiveSignals({ lang }) {
  const [n, setN] = useState(12408);
  useEffect(() => {
    const id = setInterval(() => setN((v) => v + Math.floor(Math.random() * 3) + 1), 3500);
    return () => clearInterval(id);
  }, []);
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.75 }}
      className="inline-flex items-center gap-2 rounded-full border border-orange-200/80 bg-orange-50/60 px-3.5 py-1.5"
      data-testid="hero-live-signals"
    >
      <span className="relative flex h-1.5 w-1.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#F77F11] opacity-75" />
        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#F77F11]" />
      </span>
      <span className="text-[12.5px] font-semibold text-[#9a4a08] tabular-nums">
        {n.toLocaleString(lang === 'es' ? 'es-ES' : 'en-US')}{' '}
        {lang === 'es' ? 'señales de restaurantes procesadas esta semana' : 'restaurant signals processed this week'}
      </span>
    </motion.div>
  );
}

// Home mini-scanner: hands the URL to the full Index AI scanner.
const SHOW_AI_REPORT_BAR = true;

function AiReportBar({ lang }) {
  const c = HOOK[lang] || HOOK.en;
  const [name, setName] = useState('');
  const navigate = useNavigate();
  const submit = (e) => {
    e.preventDefault();
    const v = name.trim();
    if (!v) return;
    trackEvent('free_audit_start', { source: 'home_scanner', domain: v });
    navigate(`/index-ai?scan=${encodeURIComponent(v)}`);
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay: 0.7 }}
      className="mt-9 max-w-xl"
      data-testid="hero-ai-report"
    >
      <p className="text-[17px] sm:text-[19px] font-bold tracking-[-0.01em] text-neutral-900">
        {c.hook} <span className="text-[#F77F11]">{c.hookB}</span>
      </p>
      <p className="mt-1 text-[13.5px] text-neutral-500">{c.sub}</p>
      <form
        onSubmit={submit}
        className="relative mt-4 flex flex-col sm:flex-row gap-2 sm:gap-0 sm:items-center bg-white rounded-2xl sm:rounded-full border border-neutral-200 shadow-[0_24px_70px_-26px_rgba(247,127,17,0.55)] p-2 sm:pl-5 focus-within:border-[#F77F11]/60 focus-within:shadow-[0_24px_80px_-24px_rgba(247,127,17,0.7)] transition-all"
      >
        <Search className="hidden sm:block w-[18px] h-[18px] text-neutral-400 shrink-0" aria-hidden="true" />
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={c.placeholder}
          className="flex-1 min-w-0 h-11 sm:h-12 px-4 sm:px-3 bg-transparent text-[15px] text-neutral-900 placeholder:text-neutral-400 outline-none rounded-xl"
          data-testid="hero-ai-report-input"
          aria-label={c.placeholder}
        />
        <button
          type="submit"
          className="inline-flex items-center justify-center gap-1.5 bg-[#F77F11] hover:bg-[#d8651a] text-white h-11 sm:h-12 px-6 rounded-xl sm:rounded-full text-[15px] font-semibold shadow-sm hover:shadow-md transition-all shrink-0"
          data-testid="hero-cta-primary"
        >
          <Sparkles className="w-4 h-4" />
          {c.cta}
        </button>
      </form>
      <p className="mt-2.5 text-[12px] text-neutral-400 flex items-center gap-1.5">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#F77F11] animate-pulse" />
        {c.note}
      </p>
      {/* Competitor jab → compare tool */}
      <Link to="/index-ai#compare"
        className="mt-4 group flex items-start gap-3 rounded-2xl border border-red-200/80 bg-red-50/50 hover:bg-red-50 hover:border-red-300 px-4 py-3 transition-colors"
        data-testid="hero-compare-hook">
        <Swords className="w-4 h-4 text-red-500 shrink-0 mt-0.5" aria-hidden="true" />
        <span className="text-[13.5px] text-neutral-600 leading-snug">
          <b className="text-neutral-900">{c.vsQ}</b> {c.vsB}{' '}
          <span className="font-bold text-[#d8651a] group-hover:underline underline-offset-4 whitespace-nowrap">{c.vsCta}</span>
        </span>
      </Link>
    </motion.div>
  );
}


export default function Hero() {
  const { t, lang } = useT();
  const products = PRODUCTS[lang] || PRODUCTS.en;

  // Rotating owner-style benefit headlines — the AI positioning plus what we sell.
  const rotatingTitles =
    lang === 'es'
      ? [
          ['La IA recomienda.', 'Que te elija a ti.'],
          ['Domina la búsqueda.', 'Google y ChatGPT.'],
          ['Pedidos directos.', 'Cero comisiones.'],
          ['Clientes que vuelven.', 'En automático.'],
          ['Catering 24/7.', 'Tú das el servicio.'],
        ]
      : [
          ['Diners ask AI.', 'Be the answer.'],
          ['Own the search.', 'Google & ChatGPT.'],
          ['Direct orders.', 'Zero commissions.'],
          ['Guests return.', 'On autopilot.'],
          ['Catering, sold 24/7.', 'You run service.'],
        ];
  const [titleIdx, setTitleIdx] = useState(0);
  const titleRefs = useRef([]);
  const [titleH, setTitleH] = useState(null);
  useEffect(() => {
    const delay = titleIdx === 0 ? 9000 : 6000; // first headline gets extra reading time
    const id = setTimeout(() => setTitleIdx((i) => (i + 1) % 5), delay);
    return () => clearTimeout(id);
  }, [titleIdx]);
  useEffect(() => {
    const measure = () => {
      const el = titleRefs.current[titleIdx];
      if (el) setTitleH(el.offsetHeight);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [titleIdx, lang]);

  return (
    <section id="top" className="relative pt-32 pb-16 lg:pt-40 lg:pb-28 overflow-hidden bg-white">
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(247,127,17,0.10), transparent 60%)',
        }}
      />

      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
              className="kx-eyebrow mb-6"
              data-testid="hero-category"
            >
              {t('hero.eyebrow')}
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.05 }}
              className="grid items-start text-[40px] sm:text-5xl lg:text-[60px] font-bold leading-[1.05] tracking-[-0.02em] text-neutral-900"
              style={{ height: titleH || undefined, transition: 'height 0.6s cubic-bezier(0.22, 1, 0.36, 1)', overflow: 'visible' }}
              data-testid="hero-rotating-title"
            >
              {rotatingTitles.map((pair, i) => (
                <span
                  key={pair[0]}
                  ref={(el) => { titleRefs.current[i] = el; }}
                  aria-hidden={i !== titleIdx}
                  className={`col-start-1 row-start-1 transition-all duration-700 ease-out ${
                    i === titleIdx ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3 pointer-events-none'
                  }`}
                >
                  {pair[0]}
                  <span className="block mt-2 pb-[0.15em] -mb-[0.15em] bg-gradient-to-r from-[#F77F11] to-[#ff9b4a] bg-clip-text text-transparent">
                    {pair[1]}
                  </span>
                </span>
              ))}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.6 }}
              className="mt-7 text-neutral-600 text-base sm:text-lg max-w-xl leading-relaxed"
            >
              {t('hero.body')}
            </motion.p>

            {/* Subtle credibility line */}
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.65 }}
              className="mt-3 text-neutral-500 text-[15px] max-w-xl leading-relaxed"
              data-testid="hero-credibility"
            >
              {t('hero.credibility')}
            </motion.p>

            {SHOW_AI_REPORT_BAR && <AiReportBar lang={lang} />}

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.85 }}
              className="mt-7 flex flex-wrap items-center justify-center sm:justify-start gap-x-5 gap-y-3"
            >
              <Link
                to="/services"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-1.5 text-[14.5px] font-semibold text-neutral-700 hover:text-[#F77F11] transition-colors"
              >
                {t('hero.ctaSecondary')} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <span className="hidden sm:block h-4 w-px bg-neutral-200" aria-hidden />
              <Link
                to="/how-it-works"
                className="inline-flex items-center gap-1.5 text-[14.5px] font-semibold text-neutral-700 hover:text-[#F77F11] transition-colors"
              >
                {lang === 'es' ? 'Cómo funciona' : 'How it works'} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <LiveSignals lang={lang} />
            </motion.div>

            {/* Strategic flagship products — elegant separated row */}
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.95 }}
              className="mt-8 pt-6 border-t border-neutral-200/70"
            >
              <p className="text-[10.5px] tracking-[0.2em] uppercase font-bold text-neutral-400 text-center sm:text-left">
                {lang === 'es' ? 'Las tres plataformas insignia' : 'The three flagship platforms'}
              </p>
              <div className="mt-3 flex flex-wrap items-center justify-center sm:justify-start gap-2.5" data-testid="hero-products">
              {products.map((p) => {
                const I = p.icon;
                const isSoon = p.soon;
                const inner = (
                  <>
                    <I className="w-3.5 h-3.5 text-[#F77F11]" />
                    <span>{p.label}</span>
                    {isSoon && (
                      <span className="ml-1 text-[10px] tracking-[0.14em] font-bold text-[#d8651a] bg-orange-50 border border-orange-200 rounded-full px-1.5 py-0.5 uppercase">
                        {p.soonLabel}
                      </span>
                    )}
                  </>
                );
                if (isSoon) {
                  return (
                    <span
                      key={p.label}
                      className="group inline-flex items-center gap-1.5 bg-white border border-neutral-200 rounded-full h-9 px-3.5 text-[13.5px] font-semibold text-neutral-700 cursor-default"
                      data-testid={`hero-product-${p.label.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {inner}
                    </span>
                  );
                }
                return (
                  <Link
                    key={p.label}
                    to={p.to}
                    className="group inline-flex items-center gap-1.5 bg-white border border-neutral-200 hover:border-[#F77F11] hover:bg-orange-50/40 rounded-full h-9 px-3.5 text-[13.5px] font-semibold text-neutral-700 hover:text-[#F77F11] transition-all duration-300"
                    data-testid={`hero-product-${p.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {inner}
                    <ArrowRight className="w-3 h-3 text-neutral-400 group-hover:text-[#F77F11] group-hover:translate-x-0.5 transition-all" />
                  </Link>
                );
              })}
              </div>
            </motion.div>

            {/* AI + Human philosophy — subtle one-liner, never another section */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.95 }}
              className="mt-6 text-[13.5px] text-neutral-500 italic max-w-md text-center sm:text-left mx-auto sm:mx-0"
              data-testid="hero-philosophy"
            >
              {t('hero.philosophy')}
            </motion.p>
          </div>

          <div className="relative mt-10 lg:mt-0">
            <HeroShowcase />
            <div className="hidden xl:block absolute -bottom-12 -right-4 z-10 rotate-[4deg]">
              <PhoneSimulation lang={lang} compact withCta={false} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
